import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Toggle } from '../design-system';
import { colors, spacing, typography, textStyles } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import {
  linkGoogleCalendarAccount,
  listGoogleAccounts,
  listGoogleAccountCalendars,
  linkGoogleCalendar,
  updateLinkedGoogleCalendar,
  unlinkGoogleCalendar,
  findGoogleLinkForCalendar,
  CalendarProviderAccount,
  GoogleCalendarInfo,
} from '../../services/calendar-provider.service';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface CalendarGoogleSyncProps {
  /** Only shown once the calendar itself has been saved (needs a real id to map to). */
  calendarId: string;
  /** 'business' = calendar belongs to the business; 'personal' = the user's own calendar. */
  scope: 'business' | 'personal';
  onNotify: (type: 'success' | 'error', title: string, message: string) => void;
}

const SYNC_DIRECTION_ICON: Record<string, keyof typeof Ionicons.glyphMap> = {
  inbound: 'arrow-down-circle-outline',
  outbound: 'arrow-up-circle-outline',
  both: 'swap-vertical-outline',
};

const cycleSyncDirection = (current: 'inbound' | 'outbound' | 'both'): 'inbound' | 'outbound' | 'both' =>
  current === 'inbound' ? 'outbound' : current === 'outbound' ? 'both' : 'inbound';

export const CalendarGoogleSync: React.FC<CalendarGoogleSyncProps> = ({ calendarId, scope, onNotify }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();

  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<CalendarProviderAccount[]>([]);
  const [connecting, setConnecting] = useState(false);
  const [busy, setBusy] = useState(false);

  const [showPicker, setShowPicker] = useState(false);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendarInfo[]>([]);
  const [loadingGoogleCalendars, setLoadingGoogleCalendars] = useState(false);
  const [linkingCalendarId, setLinkingCalendarId] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listGoogleAccounts(scope);
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setAccounts(res.data);
      }
    } catch (error) {
      console.error('[CalendarGoogleSync] Failed to load accounts:', error);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const link = findGoogleLinkForCalendar(accounts, calendarId);
  const existingAccount = accounts.find((a) => a.status === 'active');

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const account = await linkGoogleCalendarAccount(scope);
      if (account) {
        onNotify('success', t('calendar.googleSync.connectedTitle'), t('calendar.googleSync.connectedMessage', { email: account.externalAccountEmail }));
        await loadAccounts();
      }
    } catch (error: any) {
      onNotify('error', t('calendar.googleSync.connectFailedTitle'), error?.message || t('calendar.googleSync.genericRetry'));
    } finally {
      setConnecting(false);
    }
  };

  const loadGoogleCalendars = async (accountId: string) => {
    setLoadingGoogleCalendars(true);
    try {
      const res = await listGoogleAccountCalendars(accountId);
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setGoogleCalendars(res.data);
      }
    } catch (error) {
      console.error('[CalendarGoogleSync] Failed to load Google calendars:', error);
    } finally {
      setLoadingGoogleCalendars(false);
    }
  };

  const handleOpenPicker = async (accountId: string) => {
    const opening = !showPicker;
    setShowPicker(opening);
    if (opening) await loadGoogleCalendars(accountId);
  };

  const handleLinkCalendar = async (accountId: string, googleCalendar: GoogleCalendarInfo) => {
    setLinkingCalendarId(googleCalendar.id);
    try {
      const res = await linkGoogleCalendar(accountId, {
        externalCalendarId: googleCalendar.id,
        externalCalendarName: googleCalendar.summary,
        externalCalendarColor: googleCalendar.backgroundColor,
        localCalendarId: calendarId,
        syncDirection: 'both',
      });
      if (res.statusCode === 1000) {
        onNotify('success', t('calendar.googleSync.linkedTitle'), t('calendar.googleSync.linkedMessage', { name: googleCalendar.summary }));
        setShowPicker(false);
        await loadAccounts();
      } else {
        onNotify('error', t('calendar.googleSync.linkFailedTitle'), res.errors?.[0]?.message || t('calendar.googleSync.genericRetry'));
      }
    } catch (error: any) {
      onNotify('error', t('calendar.googleSync.linkFailedTitle'), error?.message || t('calendar.googleSync.genericRetry'));
    } finally {
      setLinkingCalendarId(null);
    }
  };

  const handleToggleSyncEnabled = async (accountId: string, externalCalendarId: string, syncEnabled: boolean) => {
    setBusy(true);
    try {
      await updateLinkedGoogleCalendar(accountId, externalCalendarId, { syncEnabled });
      await loadAccounts();
    } catch (error: any) {
      onNotify('error', t('calendar.googleSync.updateFailedTitle'), error?.message || t('calendar.googleSync.genericRetry'));
    } finally {
      setBusy(false);
    }
  };

  const handleCycleDirection = async (accountId: string, externalCalendarId: string, current: 'inbound' | 'outbound' | 'both') => {
    setBusy(true);
    try {
      await updateLinkedGoogleCalendar(accountId, externalCalendarId, { syncDirection: cycleSyncDirection(current) });
      await loadAccounts();
    } catch (error: any) {
      onNotify('error', t('calendar.googleSync.updateFailedTitle'), error?.message || t('calendar.googleSync.genericRetry'));
    } finally {
      setBusy(false);
    }
  };

  /** `reopenPicker` distinguishes "Unlink" (just stop syncing) from "Change calendar" (unlink then immediately show the picker to choose a different one). */
  const handleUnlinkCalendar = async (accountId: string, externalCalendarId: string, reopenPicker: boolean) => {
    setBusy(true);
    try {
      const res = await unlinkGoogleCalendar(accountId, externalCalendarId);
      if (res.statusCode === 1000) {
        await loadAccounts();
        if (reopenPicker) {
          setShowPicker(true);
          await loadGoogleCalendars(accountId);
        }
      } else {
        onNotify('error', t('calendar.googleSync.unlinkFailedTitle'), res.errors?.[0]?.message || t('calendar.googleSync.genericRetry'));
      }
    } catch (error: any) {
      onNotify('error', t('calendar.googleSync.unlinkFailedTitle'), error?.message || t('calendar.googleSync.genericRetry'));
    } finally {
      setBusy(false);
    }
  };

  const directionLabel = (direction: 'inbound' | 'outbound' | 'both') => {
    if (direction === 'inbound') return t('calendar.googleSync.directionInbound');
    if (direction === 'outbound') return t('calendar.googleSync.directionOutbound');
    return t('calendar.googleSync.directionBoth');
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Text style={styles.label}>{t('calendar.googleSync.title')}</Text>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{t('calendar.googleSync.title')}</Text>
      <Text style={styles.helperText}>{t('calendar.googleSync.helperText')}</Text>

      {link ? (
        <View style={styles.linkedCard}>
          <View style={[styles.linkedHeader, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="logo-google" size={20} color={colors.primary as string} />
            <Text style={[styles.linkedEmail, rtl.marginStart(spacing.sm)]} numberOfLines={1}>
              {link.account.externalAccountEmail}
            </Text>
          </View>

          <TouchableOpacity
            style={[styles.directionRow, { flexDirection: rtl.rowDirection }]}
            onPress={() => handleCycleDirection(link.account._id, link.linkedCalendar.externalCalendarId, link.linkedCalendar.syncDirection)}
            disabled={busy}
          >
            <Ionicons name={SYNC_DIRECTION_ICON[link.linkedCalendar.syncDirection]} size={18} color={colors.textLight} />
            <Text style={[styles.directionText, rtl.marginStart(spacing.xs)]}>
              {directionLabel(link.linkedCalendar.syncDirection)} · {t('calendar.googleSync.tapToChange')}
            </Text>
          </TouchableOpacity>

          <View style={[styles.enabledRow, { flexDirection: rtl.rowDirection }]}>
            <Toggle
              label={t('calendar.googleSync.syncEnabledLabel')}
              value={link.linkedCalendar.syncEnabled}
              onValueChange={(value) => handleToggleSyncEnabled(link.account._id, link.linkedCalendar.externalCalendarId, value)}
            />
          </View>

          <View style={[styles.manageRow, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity
              style={styles.manageButton}
              onPress={() => handleUnlinkCalendar(link.account._id, link.linkedCalendar.externalCalendarId, true)}
              disabled={busy}
            >
              <Ionicons name="swap-horizontal-outline" size={16} color={colors.primary as string} />
              <Text style={[styles.manageButtonText, rtl.marginStart(spacing.xs)]}>
                {t('calendar.googleSync.changeCalendar')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.manageButton}
              onPress={() => handleUnlinkCalendar(link.account._id, link.linkedCalendar.externalCalendarId, false)}
              disabled={busy}
            >
              <Ionicons name="close-circle-outline" size={16} color={colors.error as string} />
              <Text style={[styles.manageButtonText, rtl.marginStart(spacing.xs), { color: colors.error as string }]}>
                {t('calendar.googleSync.unlinkCalendar')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : existingAccount ? (
        <View style={styles.linkedCard}>
          <View style={[styles.linkedHeader, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="logo-google" size={20} color={colors.primary as string} />
            <Text style={[styles.linkedEmail, rtl.marginStart(spacing.sm)]} numberOfLines={1}>
              {existingAccount.externalAccountEmail}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.pickerToggle}
            onPress={() => handleOpenPicker(existingAccount._id)}
          >
            <Text style={styles.pickerToggleText}>
              {showPicker ? t('calendar.googleSync.hidePicker') : t('calendar.googleSync.pickCalendar')}
            </Text>
            <Ionicons name={showPicker ? 'chevron-up' : 'chevron-down'} size={18} color={colors.primary as string} />
          </TouchableOpacity>

          {showPicker && (
            <View style={styles.pickerList}>
              {loadingGoogleCalendars ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : googleCalendars.length === 0 ? (
                <Text style={styles.helperText}>{t('calendar.googleSync.noGoogleCalendars')}</Text>
              ) : (
                googleCalendars.map((gc) => (
                  <TouchableOpacity
                    key={gc.id}
                    style={[styles.pickerItem, { flexDirection: rtl.rowDirection }]}
                    onPress={() => handleLinkCalendar(existingAccount._id, gc)}
                    disabled={linkingCalendarId === gc.id}
                  >
                    <View style={[styles.pickerItemColor, gc.backgroundColor ? { backgroundColor: gc.backgroundColor } : null]} />
                    <Text style={[styles.pickerItemText, rtl.marginStart(spacing.sm)]} numberOfLines={1}>
                      {gc.summary}
                    </Text>
                    {linkingCalendarId === gc.id && <ActivityIndicator size="small" color={colors.primary} />}
                  </TouchableOpacity>
                ))
              )}
            </View>
          )}
        </View>
      ) : (
        <TouchableOpacity style={styles.connectButton} onPress={handleConnect} disabled={connecting}>
          {connecting ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : (
            <>
              <Ionicons name="logo-google" size={18} color={colors.primary as string} />
              <Text style={[styles.connectButtonText, rtl.marginStart(spacing.sm)]}>
                {t('calendar.googleSync.connectButton')}
              </Text>
            </>
          )}
        </TouchableOpacity>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    marginBottom: spacing.md,
  },
  label: {
    ...textStyles.body,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.xs,
  },
  helperText: {
    ...textStyles.caption,
    fontWeight: textStyles.caption.fontWeight as any,
    color: colors.textLight,
    marginBottom: spacing.sm,
  },
  connectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.primary,
    minHeight: 48,
  },
  connectButtonText: {
    ...textStyles.body,
    color: colors.textDark,
    fontWeight: '600',
  },
  linkedCard: {
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    backgroundColor: colors.background.primary,
    gap: spacing.sm,
  },
  linkedHeader: {
    alignItems: 'center',
  },
  linkedEmail: {
    ...textStyles.body,
    color: colors.textDark,
    fontWeight: '600',
    flex: 1,
  },
  directionRow: {
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  directionText: {
    fontSize: textStyles.caption.fontSize,
    fontFamily: textStyles.caption.fontFamily,
    lineHeight: textStyles.caption.lineHeight,
    color: colors.textLight as string,
  },
  enabledRow: {
    alignItems: 'center',
  },
  manageRow: {
    alignItems: 'center',
    gap: spacing.md,
    paddingTop: spacing.xs,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  manageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  manageButtonText: {
    fontSize: textStyles.caption.fontSize,
    fontFamily: textStyles.caption.fontFamily,
    lineHeight: textStyles.caption.lineHeight,
    color: colors.primary as string,
    fontWeight: '600',
  },
  pickerToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.xs,
  },
  pickerToggleText: {
    ...textStyles.body,
    color: colors.primary as string,
    fontWeight: '600',
  },
  pickerList: {
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  pickerItem: {
    alignItems: 'center',
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
  },
  pickerItemColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.textLight,
  },
  pickerItemText: {
    ...textStyles.body,
    fontWeight: textStyles.body.fontWeight as any,
    color: colors.textDark,
    flex: 1,
  },
});
